/* DeleteAction.js */

"use strict";

const {managerFor, refusalMessage} = require("../../../../lib/managedSchedules");

const TRIGGER_TYPE_NAME_MAX_LENGTH = 60;
const TRIGGER_TYPE_NAME_LIST_LENGTH = 10;

class DeleteAction {
    constructor(logger, gameEditor, modalUserFeedback, activeView, userSession, TriggerTypeEntity) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = modalUserFeedback;
        this._activeView = activeView;
        this._activeOrgId = userSession.activeOrganization.id;
        this._TriggerTypeEntity = TriggerTypeEntity;
        this._selectedTriggerTypeIds = [];
        this._selectedTriggerTypeNames = [];

        if (this._activeView && this._activeView.pagingFeed) {
            this._selectedTriggerTypeIds =
                this._activeView.getSelectedTriggerTypeIds().selectedRows || [];
        }

        this.run = this.run.bind(this);
    }

    run() {
        if (this._selectedTriggerTypeIds.length) {
            this._activeView.pagingFeed
                .waitForRangeIdsUpdated()
                .then(() => {
                    if (this._selectedTriggerTypeIds.length <= TRIGGER_TYPE_NAME_LIST_LENGTH) {
                        return Promise.all([
                            this._activeView.pagingFeed.getTriggerTypeEntitiesById(
                                this._selectedTriggerTypeIds
                            ),
                            this._activeView.pagingFeed.getTriggerTypeEntitiesNameById(
                                this._selectedTriggerTypeIds
                            ),
                        ]);
                    }
                    return Promise.resolve();
                })
                .then(([triggerEntities = [], selectedTriggerTypeNames = []]) => {
                    const initialSelectedLength = this._selectedTriggerTypeIds.length;
                    if (this._selectedTriggerTypeIds.length <= TRIGGER_TYPE_NAME_LIST_LENGTH) {
                        if (triggerEntities.length === 1) {
                            const triggerWorkType = triggerEntities[0].workType;
                            // Before the work-type cases: a work type does not identify an owner.
                            const manager = managerFor(triggerEntities[0]);
                            if (manager) {
                                return Promise.reject({errorCode: "MANAGED", manager});
                            }
                            if (triggerWorkType === "retrieveSupportData") {
                                return Promise.reject({
                                    errorCode: "CISCO",
                                });
                            }
                            if (triggerWorkType === "runDataRetention") {
                                return Promise.reject({
                                    errorCode: "DR",
                                });
                            }
                            if (triggerWorkType === "networkDiscovery") {
                                return Promise.reject({
                                    errorCode: "ND",
                                });
                            }
                        }
                        if (triggerEntities.length > 1) {
                            this._selectedTriggerTypeIds = triggerEntities
                                .filter((trigger) => {
                                    if (
                                        managerFor(trigger) ||
                                        trigger.workType === "retrieveSupportData" ||
                                        trigger.workType === "runDataRetention" ||
                                        trigger.workType === "networkDiscovery"
                                    ) {
                                        return false;
                                    }
                                    return true;
                                })
                                .map((trigger) => trigger.id);
                        }
                        if (this._selectedTriggerTypeIds.length === 0) {
                            return Promise.reject({
                                errorCode: "OperationErrors",
                            });
                        }
                        this._selectedTriggerTypeNames = selectedTriggerTypeNames;
                        if (
                            Array.isArray(this._selectedTriggerTypeNames) &&
                            this._selectedTriggerTypeNames.length > 0
                        ) {
                            this._message = "";
                            const settingsLink =
                                window.location.origin +
                                "#!/_app_/GameConsole/_s_/Settings/_sln_/Settings/_rd_/JTdCJTdE";
                            const networkDiscoveryLink =
                                window.location.origin +
                                "/#!/_app_/GameConsole/_s_/GameCenter/_sln_/DeviceManager/_rd_/JTdCJTdE/_exp_/network_discovery";
                            if (initialSelectedLength > this._selectedTriggerTypeIds.length) {
                                // Name the owners actually filtered out, rather than the fixed
                                // legacy list, which named a reason that did not apply to them.
                                const skippedManagers = [
                                    ...new Set(
                                        triggerEntities
                                            .map((trigger) => managerFor(trigger))
                                            .filter((manager) => manager)
                                            .map((manager) => manager.scheduleName)
                                    ),
                                ];
                                if (skippedManagers.length) {
                                    this._message +=
                                        "<p>" +
                                        skippedManagers.join(", ") +
                                        (skippedManagers.length > 1 ? " are" : " is") +
                                        " managed from System Settings and cannot be deleted here. Open the schedule to see where.</p><br />";
                                }
                                // Only claim these were skipped when one actually was.
                                const skippedLegacy = triggerEntities.some(
                                    (trigger) =>
                                        !managerFor(trigger) &&
                                        (trigger.workType === "retrieveSupportData" ||
                                            trigger.workType === "runDataRetention" ||
                                            trigger.workType === "networkDiscovery")
                                );
                                if (skippedLegacy) {
                                    this._message +=
                                        "<p>Data Retention, Cisco Support Data and Network Discovery schedules cannot be deleted. Go to <a class='sys-schedule-err' href='" +
                                        networkDiscoveryLink +
                                        "'>System Settings</a> and <a class='sys-schedule-err' href='" +
                                        settingsLink +
                                        "'>Network Discovery</a> to disable these schedules.</p><br />";
                                }
                            }
                            let s = this._selectedTriggerTypeNames.length > 1 ? "s" : "";
                            this._message +=
                                "<p>Are you sure you want to permanently delete the selected schedule" +
                                s +
                                "?</p>";
                            Object.keys(this._selectedTriggerTypeNames).forEach((id) => {
                                let triggerTypeName = this._selectedTriggerTypeNames[id];
                                let shortTriggerTypeName =
                                    triggerTypeName.length > TRIGGER_TYPE_NAME_MAX_LENGTH
                                        ? triggerTypeName.substr(0, TRIGGER_TYPE_NAME_MAX_LENGTH) +
                                          "…"
                                        : triggerTypeName;
                                this._message += "<br />" + shortTriggerTypeName;
                            });

                            return Promise.resolve();
                        } else {
                            return Promise.reject(
                                new Error("Cannot retrieve the selected schedules")
                            );
                        }
                    } else {
                        this._message = `<p>Are you sure you want to permanently delete the selected ${this._selectedTriggerTypeIds.length} schedules?</p>`;
                        return Promise.resolve();
                    }
                })
                .then(() => {
                    return this._modalUserFeedback
                        .displayConfirmation(this._message, {
                            title: "Delete",
                            enableHtml: true,
                            confirmDetails: {
                                text: "Confirm",
                                type: "destructive",
                            },
                            rejectDetails: {
                                text: "Cancel",
                                type: "text",
                            },
                        })
                        .then(() => {
                            let workTarget = {
                                method: "multi",
                                ids: this._selectedTriggerTypeIds,
                            };
                            return this._TriggerTypeEntity
                                .runDeleteTriggerTypesWork(this._activeOrgId, workTarget)
                                .then((workResults) => {
                                    //this._TriggerTypeEntity.throwWorkErrors(workResults);
                                    this._showAuditResults(workResults);
                                    this._activeView.resetSelection();

                                    if (
                                        this._activeView &&
                                        this._activeView.pagingFeed &&
                                        this._activeView.pagingFeed.feedUpdate
                                    ) {
                                        const userFilterQuery =
                                            this._activeView.pagingFeed.getFilterData()[
                                                "$userQuery"
                                            ] || "";
                                        this._activeView.pagingFeed.setFilter(userFilterQuery);
                                        this._activeView.pagingFeed.feedUpdate();
                                    }

                                    return Promise.resolve();
                                });
                        });
                })
                .catch((err) => {
                    this._logger.warn({error: err}, "DeleteAction.run() - Error");
                    let errorMsg = "";
                    const settingsBaseLink =
                        window.location.origin +
                        "#!/_app_/GameConsole/_s_/Settings/_sln_/Settings/_rd_/JTdCJTdE/_sr_/";
                    const networkDiscoveryLink =
                        window.location.origin +
                        "/#!/_app_/GameConsole/_s_/GameCenter/_sln_/DeviceManager/_rd_/JTdCJTdE/_exp_/network_discovery";
                    if (err.userRejected) {
                        return;
                    }
                    switch (err.errorCode) {
                        case "MANAGED":
                            errorMsg = refusalMessage(err.manager, "deleted");
                            break;
                        case "OperationErrors":
                            errorMsg =
                                "<p>Sorry! These schedules cannot be deleted.</p><br /><p>Go to <a class='sys-schedule-err' href='" +
                                settingsBaseLink +
                                "Organization/DataRetention'>System Settings > Data Retention</a> and disable the Data Retention schedule.<br /><br />Go to <a class='sys-schedule-err' href='" +
                                settingsBaseLink +
                                "Organization/Integrations'>System Settings > Integrations</a> and disable Cisco Support Data updates.<br /><br />Go to <a class='sys-schedule-err' href='" +
                                networkDiscoveryLink +
                                "Organization/Integrations'>Device Manager > Network Discovery</a> and open the plan, click on Schedule, and disable it.</p>";
                            break;
                        case "CISCO":
                            errorMsg =
                                "Sorry! Cisco Support Data updates cannot be deleted. Go to <a class='sys-schedule-err' href='" +
                                settingsBaseLink +
                                "Organization/Integrations'>System Settings > Integrations</a> and disable the schedule.";
                            break;
                        case "DR":
                            errorMsg =
                                "Sorry! The Data Retention schedule cannot be deleted. Go to <a class='sys-schedule-err' href='" +
                                settingsBaseLink +
                                "Organization/DataRetention'>System Settings > Data Retention</a> and disable the schedule.";
                            break;
                        case "ND":
                            errorMsg =
                                "Sorry! The Network Discovery schedule cannot be deleted. Go to <a class='sys-schedule-err' href='" +
                                networkDiscoveryLink +
                                "'>Device Manager > Network Discovery</a> and open the plan, click on Schedule, and disable it.";
                            break;
                        default:
                            errorMsg = "";
                            break;
                    }
                    if (errorMsg) {
                        return this._modalUserFeedback.displayError({
                            errorMsg,
                            errorCode: "",
                            title: "Delete",
                        });
                    } else {
                        this._modalUserFeedback.displayError(err);
                    }
                });
        } else {
            return Promise.resolve();
        }
    }

    _showAuditResults(result) {
        let userNotificationMessage = "";
        let title = "Delete Completed";
        const {successCount, failureCount} = result.summary;
        if (successCount === 1 && failureCount === 0) {
            userNotificationMessage = "Successfully deleted schedule\r\n";
        } else if (successCount === 0 && failureCount === 1) {
            userNotificationMessage = "There was a problem deleting the schedule at this time.\r\n";
            title = "Delete Unsuccessful";
        } else {
            userNotificationMessage = [
                `Successfully deleted: ${successCount || 0}`,
                `Failed to delete: ${failureCount || 0}`,
            ].join("\r\n");
        }

        return this._modalUserFeedback.displayNotification(userNotificationMessage, {
            title: title,
        });
    }
}

module.exports = DeleteAction;

DeleteAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "gameEditor.activeView",
    "$teve.userSession",
    "$teve.entities.TriggerType",
];

DeleteAction.$displayDetails = {
    type: "Action",
    name: "DeleteAction",
    icon: "common.trash_bin",
    shown: true,
    title: "Delete Schedules",
    order: 5,
};
